import React, { createContext, useContext, useState, useEffect } from 'react';

const AttendanceContext = createContext(null);

export function AttendanceProvider({ children }) {
  const [records, setRecords] = useState([]);
  const [punchedIn, setPunchedIn] = useState(false);
  const [todayPunchIn, setTodayPunchIn] = useState(null);
  const [todayPunchOut, setTodayPunchOut] = useState(null);
  const [leaves, setLeaves] = useState([]);
  const [queries, setQueries] = useState([]);

  useEffect(() => {
    const storedRecords  = localStorage.getItem('ems_attendance');
    const storedLeaves   = localStorage.getItem('ems_leaves');
    const storedQueries  = localStorage.getItem('ems_queries');
    const storedPunchState = localStorage.getItem('ems_punch_state');

    if (storedRecords)  setRecords(JSON.parse(storedRecords));
    if (storedLeaves)   setLeaves(JSON.parse(storedLeaves));
    if (storedQueries)  setQueries(JSON.parse(storedQueries));

    if (storedPunchState) {
      const ps = JSON.parse(storedPunchState);
      const today = new Date().toDateString();
      if (ps.date === today) {
        setPunchedIn(ps.punchedIn);
        setTodayPunchIn(ps.punchIn);
        setTodayPunchOut(ps.punchOut);
      }
    }
  }, []);

  const saveRecords = (updated) => {
    localStorage.setItem('ems_attendance', JSON.stringify(updated));
    setRecords(updated);
  };

  const punchIn = ({ photo = null, location = null } = {}) => {
    const now  = new Date();
    const time = now.toISOString();
    setTodayPunchIn(time);
    setTodayPunchOut(null);
    setPunchedIn(true);

    localStorage.setItem('ems_punch_state', JSON.stringify({
      date:      now.toDateString(),
      punchedIn: true,
      punchIn:   time,
      punchOut:  null,
      photo,
      location,
    }));
  };

  const punchOut = () => {
    const now  = new Date();
    const time = now.toISOString();
    setTodayPunchOut(time);
    setPunchedIn(false);

    // Retrieve photo/location saved at punch-in
    let savedPhoto    = null;
    let savedLocation = null;
    try {
      const ps  = JSON.parse(localStorage.getItem('ems_punch_state') || '{}');
      savedPhoto    = ps.photo    || null;
      savedLocation = ps.location || null;
    } catch (_) {}

    // Every punch-out creates a NEW individual session record
    const dateStr = now.toLocaleDateString('en-IN', {
      year: 'numeric', month: 'short', day: 'numeric',
    });

    const entry = {
      id:       Date.now(),           // unique id per session
      date:     dateStr,
      rawDate:  todayPunchIn,
      punchIn:  todayPunchIn,
      punchOut: time,
      status:   'Present',
      photo:    savedPhoto,
      location: savedLocation,
    };

    // Always prepend — multiple records per day are allowed
    const updated = [entry, ...records];
    saveRecords(updated);

    localStorage.setItem('ems_punch_state', JSON.stringify({
      date:      now.toDateString(),
      punchedIn: false,
      punchIn:   todayPunchIn,
      punchOut:  time,
      photo:     savedPhoto,
      location:  savedLocation,
    }));
  };

  const applyLeave = (leaveData) => {
    const updated = [{
      ...leaveData,
      id:        Date.now(),
      status:    'Pending',
      appliedOn: new Date().toISOString(),
    }, ...leaves];
    setLeaves(updated);
    localStorage.setItem('ems_leaves', JSON.stringify(updated));
  };

  const submitQuery = (queryData) => {
    const updated = [{
      ...queryData,
      id:          Date.now(),
      status:      'Open',
      submittedOn: new Date().toISOString(),
    }, ...queries];
    setQueries(updated);
    localStorage.setItem('ems_queries', JSON.stringify(updated));
  };

  return (
    <AttendanceContext.Provider value={{
      records, punchedIn, todayPunchIn, todayPunchOut,
      punchIn, punchOut, leaves, applyLeave, queries, submitQuery,
    }}>
      {children}
    </AttendanceContext.Provider>
  );
}

export function useAttendance() {
  return useContext(AttendanceContext);
}